'use client'

export const dynamic = 'force-dynamic'

import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import {
  ArrowRight, BadgeCheck, Briefcase, HeartHandshake, Landmark, MapPin, Megaphone, Search, Sparkles, Store,
} from 'lucide-react'
import { EmptyState, PageHero } from '../../components/ui/kit'

/* ─────────────────────────────────────────────────────────────
   SEARCH (Phase 1 batch 3) — honest cross-record search. The old
   page ran on the synthetic civic-graph engine with fabricated
   trust/confidence scores. This one searches only real records:
   live masjids (DB+seed via /api/masjids), campaigns, notices,
   jobs and businesses. NL masjid queries route to the directory's
   AI smart search.
───────────────────────────────────────────────────────────── */

interface Result {
  id: string
  domain: 'Masjid' | 'Campaign' | 'Notice' | 'Job' | 'Business'
  title: string
  detail: string
  href: string
  verified?: boolean
}

const DOMAIN_ICON = { Masjid: Landmark, Campaign: HeartHandshake, Notice: Megaphone, Job: Briefcase, Business: Store } as const
const SUGGESTIONS = ['Charminar', 'Quran classes', 'renovation campaign', 'imam jobs', 'Mumbai']

function searchRecords(_q: string): Result[] {
  // H5: cross-domain search over DEMO_CAMPAIGNS/ANNOUNCEMENTS/JOBS/BUSINESSES removed (all
  // fabricated). Honest-empty until wired to the real tables. The real MASJID search (live API,
  // in SearchPage) is unaffected. Same principle as H3/H4.
  return []
}

export default function SearchPage() {
  const [input, setInput] = useState('')
  const [query, setQuery] = useState('')
  const [masjids, setMasjids] = useState<Result[]>([])
  const [loading, setLoading] = useState(false)
  const seqRef = useRef(0)

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q')?.trim() || ''
    setInput(q)
    setQuery(q)
  }, [])

  useEffect(() => {
    if (!query) { setMasjids([]); return }
    const seq = ++seqRef.current
    setLoading(true)
    fetch(`/api/masjids?q=${encodeURIComponent(query)}&limit=8`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (seq !== seqRef.current) return
        setMasjids((d?.masjids || []).map((m: any) => ({
          id: `m-${m.slug}`,
          domain: 'Masjid' as const,
          title: m.nameEnglish,
          detail: [m.district, m.city].filter(Boolean).join(', ') || 'India',
          href: `/masjids/${m.slug}`,
          verified: Boolean(m.verified),
        })))
      })
      .catch(() => {})
      .finally(() => { if (seq === seqRef.current) setLoading(false) })
  }, [query])

  const records = useMemo(() => (query ? searchRecords(query) : []), [query])
  const all = [...masjids, ...records]
  const submit = (e?: React.FormEvent) => {
    e?.preventDefault()
    const q = input.trim()
    setQuery(q)
    try { window.history.replaceState(null, '', q ? `/search?q=${encodeURIComponent(q)}` : '/search') } catch {}
  }

  return (
    <main className="srp">
      <PageHero
        compact
        motif="directory"
        eyebrow="Search"
        title={query ? `Results for “${query}”` : 'Search the network.'}
        subtitle="Masjids, campaigns, notices, jobs and businesses — real records only."
        actions={<div className="srp-hero-tools">
          <form className="srp-form" onSubmit={submit}>
            <Search size={17} strokeWidth={1.5} />
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Search masjids, campaigns, notices, jobs…" />
            <button type="submit">Search <ArrowRight size={14} strokeWidth={1.5} /></button>
          </form>
          <div className="srp-chips">
            {SUGGESTIONS.map((s) => (
              <button key={s} type="button" onClick={() => { setInput(s); setQuery(s) }}>{s}</button>
            ))}
            <Link href={`/directory${query ? `?q=${encodeURIComponent(query)}` : ''}`} className="srp-smart">
              <Sparkles size={12} strokeWidth={1.5} /> Try AI smart search →
            </Link>
          </div>
        </div>}
      />

      <div className="srp-wrap srp-body">
        {!query ? (
          <EmptyState
            icon={<Search size={20} strokeWidth={1.5} />}
            title="Type to search everything"
            body="One box across every mapped masjid, live campaigns, committee notices, jobs and local businesses."
            cta={{ label: 'Browse the directory', href: '/directory' }}
          />
        ) : loading && !all.length ? (
          <div className="srp-skeletons">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="srp-skeleton" />)}</div>
        ) : all.length ? (
          <div className="srp-list">
            {all.map((r) => {
              const Icon = DOMAIN_ICON[r.domain]
              return (
                <Link key={r.id} href={r.href} className="srp-row">
                  <span className="srp-ic"><Icon size={16} strokeWidth={1.5} /></span>
                  <span className="srp-main">
                    <b>{r.title} {r.verified && <BadgeCheck size={13} strokeWidth={1.5} />}</b>
                    <i><MapPin size={10} strokeWidth={1.5} /> {r.detail}</i>
                  </span>
                  <span className="srp-domain">{r.domain.toUpperCase()}</span>
                  <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              )
            })}
          </div>
        ) : (
          <EmptyState
            icon={<Search size={20} strokeWidth={1.5} />}
            title={`Nothing matched “${query}”`}
            body="Try a masjid name, a locality, a campaign type or a job role — or let AI read your query on the directory."
            cta={{ label: 'Try AI smart search', href: `/directory?q=${encodeURIComponent(query)}` }}
          />
        )}
      </div>

      <style jsx>{`
        .srp{background:var(--f-beige);}
        .srp-wrap{max-width:820px;margin:0 auto;padding:0 26px}
        .srp-hero{position:relative;z-index:0;padding:42px 0 34px}
        .srp-hero-bg{position:absolute;left:0;right:0;top:0;bottom:-110px;z-index:-1;pointer-events:none;
          background:url('/hero-art/dome.svg') right -70px bottom -20px / 520px auto no-repeat,radial-gradient(560px 320px at 18% 30%, rgba(14,124,97,.14), transparent 62%),linear-gradient(170deg,#101A38,#0B132B);
          -webkit-mask-image:linear-gradient(180deg,#000 0%,#000 calc(100% - 150px),transparent 100%);
          mask-image:linear-gradient(180deg,#000 0%,#000 calc(100% - 150px),transparent 100%)}
        .srp-pattern{position:absolute;inset:0;opacity:.04;background-image:radial-gradient(rgba(255,255,255,.85) 1px,transparent 1.5px);background-size:26px 26px}
        .srp-eyebrow{display:inline-flex;align-items:center;gap:8px;font-family:var(--f-mono);font-size:10px;letter-spacing:.18em;color:#14B8A6;border:1px solid rgba(20,184,166,.35);border-radius:99px;padding:4px 12px;background:rgba(20,184,166,.08);margin-bottom:12px}
        .srp-hero h1{font-family:var(--f-display);font-size:clamp(24px,2.8vw,34px);font-weight:700;color:#F6F4EE;letter-spacing:-.01em}
        .srp-hero p{color:rgba(246,244,238,.75);font-size:14px;margin-top:8px}
        .srp-form{display:flex;align-items:center;gap:10px;margin-top:18px;background:#fff;border:1px solid rgba(14,124,97,.25);border-radius:14px;padding:8px 8px 8px 16px;box-shadow:0 10px 30px rgba(5,10,25,.25);color:var(--f-green)}
        .srp-form input{flex:1;min-width:0;border:none;outline:none;background:none;font-size:14.5px;font-family:inherit;color:var(--f-ink)}
        .srp-form button{display:inline-flex;align-items:center;gap:7px;background:var(--f-green);color:#fff;border:none;border-radius:10px;padding:11px 18px;font-size:13px;font-weight:600;cursor:pointer;font-family:inherit}
        .srp-chips{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:13px}
        .srp-chips button{border:1px solid rgba(246,244,238,.25);border-radius:99px;background:rgba(246,244,238,.07);color:rgba(246,244,238,.85);font-size:11.5px;font-weight:500;padding:6px 14px;cursor:pointer;font-family:inherit;transition:all .2s}
        .srp-chips button:hover{background:rgba(246,244,238,.14)}
        .srp-chips :global(.srp-smart){display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:600;color:#2DD4BF;text-decoration:none;margin-left:auto}

        .srp-body{padding:6px 26px 54px}
        .srp-list{display:grid;gap:8px}
        .srp-body :global(.srp-row){display:flex;align-items:center;gap:13px;background:#fff;border:1px solid var(--f-line);border-radius:13px;padding:12px 16px;box-shadow:var(--f-shadow);text-decoration:none;color:var(--f-slate-dim);transition:transform .2s,box-shadow .2s}
        .srp-body :global(.srp-row:hover){transform:translateY(-2px);box-shadow:var(--f-shadow-lg)}
        .srp-body :global(.srp-ic){width:34px;height:34px;border-radius:10px;background:rgba(14,124,97,.08);color:var(--f-green);display:grid;place-items:center;flex:none}
        .srp-body :global(.srp-main){flex:1;min-width:0;display:flex;flex-direction:column;gap:1px}
        .srp-body :global(.srp-main b){display:inline-flex;align-items:center;gap:5px;font-family:var(--f-display);font-size:13.5px;font-weight:600;color:var(--f-navy)}
        .srp-body :global(.srp-main i){display:inline-flex;align-items:center;gap:4px;font-style:normal;font-size:11px;color:var(--f-slate)}
        .srp-body :global(.srp-domain){font-family:var(--f-mono);font-size:8.5px;letter-spacing:.1em;color:var(--f-green);background:rgba(14,124,97,.08);border-radius:99px;padding:4px 10px;font-weight:600;flex:none}

        .srp-skeletons{display:grid;gap:8px;margin-top:8px}
        .srp-skeleton{height:58px;border-radius:13px;background:linear-gradient(100deg,#EFECE3 30%,#F7F4EC 50%,#EFECE3 70%);background-size:200% 100%;animation:srsk 1.4s linear infinite}
        @keyframes srsk{from{background-position:200% 0}to{background-position:-200% 0}}
        @media(prefers-reduced-motion:reduce){.srp-skeleton{animation:none}}
      `}</style>
    </main>
  )
}
